import {
    Alert,
    Autocomplete,
    Button,
    Checkbox,
    Chip,
    FormControl,
    FormControlLabel,
    Grid,
    TextField,
    Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { GET, POST } from "../../../../Utilities/BaseService";
import UpdatedSearchableInput from "../../../../Utilities/UpdatedSearchableInput";

const AddReadingCollectionOnOff = () => {
    const [filter, setFilter] = useState({
        district: "",
        powerstation: "",
    });

    const [feedback, setFeedback] = useState({
        severity: "",
        show: false,
        message: "",
        loading: false,
    });

    const [selectedUsers, setSelectedUsers] = useState<any[]>([]);

    const { data: usersRes, isLoading: usersLoading } = useQuery(
        ["reading-bill-on-off-users"],
        () => GET("/user", { page: 1, limit: 10000 })
    );

    const users = usersRes?.data?.rows ?? [];

    const [toggles, setToggles] = useState({
        readings: false,
        bills: false,
        collections: false,
    });

    const [submitting, setSubmitting] = useState(false);

    const handleToggle = (field: "readings" | "bills" | "collections") => {
        setToggles((prev) => ({
            ...prev,
            [field]: !prev[field],
        }));
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const payload = {
                district: filter.district,
                powerstation: filter.powerstation,
                ...toggles,
                users: selectedUsers.map((user) => user.id),
            };

            const response = await POST("/reading/bill-on-off/create", payload);

            setFeedback({
                severity: "success",
                loading: false,
                message: response.data?.message || "Data inserted successfully",
                show: true,
            });
        } catch (error: any) {
            console.error("Submit failed:", error);
            setFeedback({
                severity: "error",
                loading: false,
                message:
                    error?.response?.data?.message || "Something went wrong",
                show: true,
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Function to render a "TextField-like" checkbox
    const RenderCheckboxField = (
        label: string,
        field: "readings" | "bills" | "collections"
    ) => (
        <FormControl fullWidth sx={{ mb: 2 }}>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={toggles[field]}
                        onChange={() => handleToggle(field)}
                        sx={{
                            color: "error.main",
                            "&.Mui-checked": {
                                color: "success.main",
                            },
                        }}
                        size="small"
                    />
                }
                label={label}
                sx={{
                    border: 1,
                    borderColor: "grey.400",
                    borderRadius: 1,

                    width: "100%",
                    height: "30%",
                    "&:hover": {
                        borderColor: "primary.main",
                    },
                }}
            />
        </FormControl>
    );

    return (
        <div style={{ padding: 20 }}>
            <Typography variant="h5" gutterBottom>
                Activate/ Deactivate Readings Bills & Collections
            </Typography>
            <Grid container spacing={2}>
                <Grid xs={12} md={6} lg={3} item>
                    <UpdatedSearchableInput
                        api="/region/district"
                        label="District"
                        filter={filter}
                        setFilter={setFilter}
                    />
                </Grid>

                <Grid xs={12} md={6} lg={3} item>
                    <UpdatedSearchableInput
                        api="/powerstation"
                        label="Powerstation"
                        filter={filter}
                        setFilter={setFilter}
                        dep={filter.district}
                        params={{ district: filter.district }}
                    />
                </Grid>

                <Grid xs={12} md={6} lg={3} item>
                    <Autocomplete
                        multiple
                        options={users}
                        getOptionLabel={(option: any) =>
                            option.name || option.username || "User"
                        }
                        value={selectedUsers}
                        onChange={(_, newValue) => setSelectedUsers(newValue)}
                        filterSelectedOptions
                        loading={usersLoading}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Assign Users"
                                placeholder="Select users"
                                size="small"
                                fullWidth
                            />
                        )}
                        renderTags={(value: any[], getTagProps) =>
                            value.map((option: any, index: number) => (
                                <Chip
                                    label={option.name || option.username}
                                    {...getTagProps({ index })}
                                    key={option.id}
                                />
                            ))
                        }
                    />
                </Grid>

                <Grid xs={12} md={4} lg={2} item>
                    {RenderCheckboxField("Readings", "readings")}
                </Grid>

                <Grid xs={12} md={4} lg={2} item>
                    {RenderCheckboxField("Bills", "bills")}
                </Grid>

                <Grid xs={12} md={4} lg={2} item>
                    {RenderCheckboxField("Collections", "collections")}
                </Grid>

                {feedback.show && (
                    <Alert
                        severity={feedback.severity as any}
                        style={{ width: "100%", height: "100%" }}
                    >
                        {feedback.message}
                    </Alert>
                )}

                <Grid xs={12} item>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleSubmit}
                        disabled={submitting}
                    >
                        {submitting ? "Submitting..." : "Submit"}
                    </Button>
                </Grid>
            </Grid>
        </div>
    );
};

export default AddReadingCollectionOnOff;
